import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastmessageService } from 'src/app/toastmessage.service';
import { ConfirmationMessageDialogComponent } from '../confirmation-message-dialog/confirmation-message-dialog.component';
import { MessService } from '../mess.service';

export interface ApplyHolidayDialogData {
  messBuildingId: unknown;
  /** Current stock / messReport daysheetDate (e.g. 26 May 2026). */
  daysheetDate?: string;
  /** Earliest selectable date from messReport daysheetDate (e.g. 26 May 2026). */
  minHolidayDate?: Date | string;
}

@Component({
  selector: 'app-apply-holiday',
  templateUrl: './apply-holiday.component.html',
  styleUrls: ['./apply-holiday.component.scss'],
})
export class ApplyHolidayComponent implements OnInit {
  holidayFromDate: any = null;
  holidayToDate: any = null;
  holidayReason = '';
  holidayOtherText = '';
  holidayDetails: any[] = [];
  minHolidayDate: Date | null = null;

  constructor(
    private dialogRef: MatDialogRef<ApplyHolidayComponent>,
    private matDialog: MatDialog,
    private service: MessService,
    private spinner: NgxSpinnerService,
    private toast: ToastmessageService,
    @Inject(MAT_DIALOG_DATA) public data: ApplyHolidayDialogData,
  ) {console.log('data', this.data)}

  ngOnInit(): void {
    this.syncMinHolidayDate();
    this.getHolidayDetails();
  }

  /** Disable dates before messReport daysheetDate (back dates). */
  holidayDateFilter = (date: Date | null): boolean => {
    if (!date) {
      return false;
    }
    const min = this.minHolidayDate
      ? moment(this.minHolidayDate).startOf('day')
      : moment().startOf('day');
    return moment(date).startOf('day').isSameOrAfter(min, 'day');
  };

  close(): void {
    this.resetForm();
    this.dialogRef.close(false);
  }

  private syncMinHolidayDate(): void {
    const fromDaysheet = String(this.data?.daysheetDate ?? '').trim();
    if (fromDaysheet) {
      const parsed = moment(fromDaysheet, 'DD MMM YYYY', true);
      const mm = parsed.isValid() ? parsed : moment(fromDaysheet);
      if (mm.isValid()) {
        this.minHolidayDate = mm.startOf('day').toDate();
        this.enforceMinHolidayDates();
        return;
      }
    }

    const raw = this.data?.minHolidayDate;
    if (raw == null) {
      // No daysheetDate passed — still block calendar back-dates before today
      this.minHolidayDate = moment().startOf('day').toDate();
      this.enforceMinHolidayDates();
      return;
    }
    const parsed = moment(raw);
    this.minHolidayDate = parsed.isValid() ? parsed.startOf('day').toDate() : moment().startOf('day').toDate();
    this.enforceMinHolidayDates();
  }

  private enforceMinHolidayDates(): void {
    if (!this.minHolidayDate) {
      return;
    }
    const min = moment(this.minHolidayDate).startOf('day');
    if (this.holidayFromDate && moment(this.holidayFromDate).startOf('day').isBefore(min, 'day')) {
      this.holidayFromDate = null;
    }
    if (this.holidayToDate && moment(this.holidayToDate).startOf('day').isBefore(min, 'day')) {
      this.holidayToDate = null;
    }
  }

  private resetForm(): void {
    this.holidayFromDate = null;
    this.holidayToDate = null;
    this.holidayReason = '';
    this.holidayOtherText = '';
  }

  getHolidayDetails(): void {
    this.spinner.show();
    this.service.holidayDetails().subscribe({
      next: (res: any) => {
        if (!res) {
          return;
          this.spinner.hide();
        }
        this.holidayDetails = res;
        console.log('this.holidayDetails', this.holidayDetails);
        this.spinner.hide();
      },
      error: () => {
        this.spinner.hide();
      },
    });
  }

  submitHoliday(): void {
    const validation = this.validateHolidayForm();
    if (!validation.valid) {
      this.toast.showError(validation.message ?? 'Please complete all holiday fields.');
      return;
    }

    const confirmRef = this.matDialog.open(ConfirmationMessageDialogComponent, {
      width: '400px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: true,
      data: {
        title: 'Submit Holiday',
        message: 'Are you sure you want to submit this holiday?',
        confirmLabel: 'Submit',
        confirmResult: 'submit',
      },
    });
    confirmRef.afterClosed().subscribe((result) => {
      if (result === 'submit' && validation.payload) {
        this.confirmSubmitHoliday(validation.payload);
      }
    });
  }

  private validateHolidayForm():
    | { valid: true; payload: Record<string, unknown> }
    | { valid: false; message: string } {
    const from = this.holidayFromDate ? moment(this.holidayFromDate) : null;
    const to = this.holidayToDate ? moment(this.holidayToDate) : null;
    if (!from?.isValid() || !to?.isValid()) {
      return { valid: false, message: 'Please select From date and To date.' };
    }
    if (this.minHolidayDate) {
      const min = moment(this.minHolidayDate).startOf('day');
      if (from.isBefore(min, 'day') || to.isBefore(min, 'day')) {
        return {
          valid: false,
          message: `Please select dates on or after ${min.format('DD MMM YYYY')}.`,
        };
      }
    }
    if (to.isBefore(from, 'day')) {
      return { valid: false, message: 'To date must be greater than or equal to From date.' };
    }
    const selected = String(this.holidayReason ?? '').trim();
    if (!selected) {
      return { valid: false, message: 'Please select a reason.' };
    }
    const reasonText =
      selected === 'OTHER'
        ? String(this.holidayOtherText ?? '').trim()
        : selected;
    if (!reasonText) {
      return { valid: false, message: 'Please enter other reason.' };
    }

    return {
      valid: true,
      payload: {
        fromDate: from.format('DD MMM YYYY').toLowerCase(),
        toDate: to.format('DD MMM YYYY').toLowerCase(),
        holidayReason: reasonText,
        messBuildingId: this.data?.messBuildingId,
      },
    };
  }

  private confirmSubmitHoliday(payload: Record<string, unknown>): void {
    this.spinner.show();
    console.log('holiday payload', payload);
    this.service.saveHolidayDetails(payload).subscribe({
      next: (res: any) => {
        if (!res) {
          this.spinner.hide();
          return;
        }
        console.log('res', res);
        this.toast.showSuccess('Holiday Submited Successfully.');
        this.resetForm();
        this.dialogRef.close(true);
        this.spinner.hide();
      },
      error: (err: any) => {
        console.log('err', err);
        this.toast.showError(err.error.message);
        this.spinner.hide();
      },
    });
  }
}
